import {gql} from '@apollo/client';
import {useMutation} from '@apollo/client/react';
import { TValidator } from "@shared/types";
import { Box, MenuItem, Select } from "@mui/material";
import { FunctionComponent } from "react";
import IcIcon from "../IcIcon";

import { DynamicVariables } from "./DynamicVariables/DynamicVariables/DynamicVariables";

const comparitors = ["=", ">", "<", ">=", "<=", "!=", "exists", "not_exists"];

const comparatorsIcon: Record<string, any> = {
  "=": "mdi:equal",
  ">": "mdi:greater-than",
  "<": "mdi:less-than",
  ">=": "mdi:greater-than-or-equal",
  "<=": "mdi:less-than-or-equal",
  "!=": "mdi:not-equal-variant",
};

const noOutput = ["exists", "not_exists"];

const cNames: Record<string, string> = {
  exists: "Exists",
  not_exists: "Not Exists",
};

const UPDATE_VALIDATOR = gql`
  mutation UpdateVariableInput(
    $input: DynamicMaterialListUpdateValidatorInput!
  ) {
    dynamicMaterialList_UpdateValidator(input: $input) {
      id
      input
      output
    }
  }
`;
const REMOVE_DYNAMIC_VARIABLE = gql`
  mutation RemoveDynamicVariable($input: DeleteDynamicVariableInput!) {
    removeDynamicVariable(input: $input)
  }
`;

export const Validator: FunctionComponent<{
  edit?: boolean;
  onChange: (validator: TValidator, changed: Partial<TValidator>) => void;
  productId: string;
  parentProductId?: string;
  validator: TValidator;
  onVariableDelete?: (variableId?: string) => any;
  listId?: string;
}> = ({
  validator,
  onChange,
  productId,
  listId,
  onVariableDelete,
  parentProductId,
}) => {
  const { id, input, comparator, output } = validator;
  const [updateValidator] = useMutation(UPDATE_VALIDATOR);
  const [removeDynamicVariable] = useMutation(REMOVE_DYNAMIC_VARIABLE);

  const onVariableDeleteDefault = (variableId?: string) => {
    if (variableId) {
      const newInput = input.filter((i) => i !== variableId);
      const newOutput = output?.filter((i) => i !== variableId);
      updateValidator({
        variables: {
          input: {
            id,
            dynamicMaterialListId: listId,
            input: newInput,
            output: newOutput,
          },
        },
      });
    }
  };

  const onVariableDeleteTrue = onVariableDelete || onVariableDeleteDefault;

  return (
    <Box>
      <DynamicVariables
        productId={productId}
        parentProductId={parentProductId}
        onChange={async (input) => {
          await onChange({ ...validator, input }, { input });
        }}
        variables={input}
        onVariableDelete={(variableId) => {
          onVariableDeleteTrue(variableId);
        }}
        removeDynamicVariable={removeDynamicVariable}
      />
      <Box sx={{ display: "inline-flex", verticalAlign: "middle", margin: 2 }}>
        <Box>
          <Select
            size="small"
            value={comparator}
            onChange={(v) => {
              onChange(
                { ...validator, comparator },
                { comparator: v.target.value as any }
              );
            }}
          >
            {comparitors.map((c, i) => {
              const notComparator = cNames[c];

              if (notComparator) {
                return (
                  <MenuItem
                    value={c}
                    key={`${c}_${i}`}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    {notComparator}
                  </MenuItem>
                );
              } else {
                return (
                  <MenuItem
                    value={c}
                    key={`${c}_${i}`}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <IcIcon noWrap icon={comparatorsIcon[c]} fontSize={13} />
                  </MenuItem>
                );
              }
            })}
          </Select>
        </Box>
      </Box>
      {!noOutput.includes(comparator) && (
        <DynamicVariables
          outputVariant="string"
          parentProductId={parentProductId}
          productId={productId}
          withStaticSubCategory
          onChange={async (output) => {
            await onChange({ ...validator, output }, { output });
          }}
          onVariableDelete={(variableId) => {
            onVariableDeleteTrue(variableId);
          }}
          variables={output || []}
          removeDynamicVariable={removeDynamicVariable}
        />
      )}
    </Box>
  );
};
